import { type ReactElement, type RefObject, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useSnapshot } from 'valtio'
import { globalUserProxy } from '../../../../../proxies/globalUserProxy'
import { closeAccountDropdown } from '../../../../../proxies/globalLayoutProxy'
import { APP_SETTINGS_ACCOUNT_PATH } from '../../../../../../pages/app/settings/account'
import { APP_DATA_EXPORTS_PATH } from '../../../../../../pages/app/data-exports'
import { handleSignOut } from './utils'

const COMMON_MENU_ITEM_CLASSES = 'text-gray-700 flex justify-between w-full px-4 py-2 text-sm leading-5 text-left active:text-gray-600 hover:bg-wpy-light-gray-400 cursor-pointer'

interface ExpandedDropdownProps {
  parentRef: RefObject<HTMLButtonElement>
}

export function ExpandedDropdown ({ parentRef }: ExpandedDropdownProps): ReactElement {
  const { user } = useSnapshot(globalUserProxy)
  const ref = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    function handleClickOutside (event: MouseEvent): void {
      if (!event.target) { return }

      if (!(event.target instanceof Node)) { return }
      const outsideDropdown = ref.current && !ref.current.contains(event.target)
      const outsideParent = parentRef.current && !parentRef.current.contains(event.target)
      if (outsideDropdown && outsideParent) {
        closeAccountDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => { document.removeEventListener('mousedown', handleClickOutside) }
  }, [ref, parentRef])

  return (
    <div
      className="relative transition-all duration-300 transform origin-top-right translate-y-0 scale-95 z-60 translate-x-0"
      ref={ref}
    >
      <div
        className="absolute right-0 w-64 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg outline-none z-60"
        role="menu"
      >
        <div className="px-4 py-3">
          <p className="text-sm leading-5">Signed in as</p>
          <p className="text-sm font-medium leading-5 text-gray-900 truncate">{user?.email ?? 'Unknown Email'}</p>
        </div>
        <Link href={APP_DATA_EXPORTS_PATH}>
          <div
            onClick={closeAccountDropdown}
            className={COMMON_MENU_ITEM_CLASSES}
            role="menuitem"
          >
            Data Exports
          </div>
        </Link>
        <Link href={APP_SETTINGS_ACCOUNT_PATH}>
          <div
            onClick={closeAccountDropdown}
            className={COMMON_MENU_ITEM_CLASSES}
            role="menuitem"
          >
            Account settings
          </div>
        </Link>
        <a
          className={COMMON_MENU_ITEM_CLASSES}
          role="menuitem"
          onClick={handleSignOut}
        >
          Sign out
        </a>
      </div>
    </div>
  )
}
